<template>
  <div class="idou">
    <h3>異動届</h3>
    <template v-if="form.idou">
      <div class="item">
        <b v-if="form.idou.todokede_kubun != OptionConst.TODOKEDE_KUBUN.HANNICHI_HOSEI" class="title required">異動日</b>
        <b v-else class="title required">半日補正日</b>
        <date-picker v-model="form.idou.idou_date" :textInput="false"
          :error="getError(form.idou, 'idou_date')"></date-picker>
      </div>

      <div class="item">
        <koyouKeiyakuTimeSelect v-if="isInit" :errors="form.errors" :targetDate="form.idou.idou_date"
          :koyouKeiyakuId="koyouKeiyakuId" :workTimeId="workTimeId" @change="changeKoyouKeiyakuTime"
          :hideWorkTime="true">
        </koyouKeiyakuTimeSelect>
      </div>

      <div class="item">
        <b class="title required">届出区分</b>
        <select-box :options="todokedeKubunOption" @change="removeAllError(form.idou)"
          :error="getError(form.idou, 'todokede_kubun')" :hasBlank="true"
          v-model="form.idou.todokede_kubun"></select-box>
      </div>

      <component :is="todokede" :idou="form.idou"></component>

      <div class="item">
        <b class="title">備考</b>
        <input-textarea v-model="form.notes" :error="getError(form, 'notes')"></input-textarea>
      </div>
    </template>
  </div>
</template>

<script lang="ts">

import { defineComponent, computed, onMounted, nextTick, toRefs, ref, Ref } from 'vue';
import { useForm } from '@/composable/form/form';
import OptionConst from '@/const/Option';
import { Shinsei, ShinseiKoyouKeiyakuTime } from '@/types/Shinsei';
import AddressChange from "@/components/shinsei/create/parts/idou_parts/AddressChange.vue";
import NameChange from "@/components/shinsei/create/parts/idou_parts/NameChange.vue";
import Other from "@/components/shinsei/create/parts/idou_parts/Other.vue";
import { useAuthStore } from '@/store/auth';
import { Logger } from '@/utils/Logger';
import { KoyouKeiyakuTimeSelectEmitData, SyuugyouBasyoAndKoyouKeiyakuOption } from '@/types/SyuugyouBasyo';
import KoyouKeiyakuTimeSelect from "@/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue"

export default defineComponent({
  props: {
    form: {
      type: Object as () => Shinsei,
      default: null
    },
  },
  emits: {
    changeKoyouKeiyakuTime: (selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null, selectedKoyouKeiyakuTimeOption: ShinseiKoyouKeiyakuTime | null) => true,
  },
  setup(props, context) {
    const { form } = toRefs(props)

    // 編集モードで使用する就業場所（雇用契約）と就業時間の初期値
    let workTimeId: Ref<number | null> = ref(null)
    let koyouKeiyakuId: Ref<number | null> = ref(null)

    const isInit = ref(false)

    // エラー取得
    const { getError, removeAllError } = useForm();

    onMounted(async () => {
      Logger.debug('----------------------------\n異動届コンポーネントの初期化開始')
      Logger.debug('異動届 onMounted', { '申請': form.value })
      nextTick(() => {
        Logger.debug('異動届 onMounted->nextTick', { '申請': form.value })
        if (!form.value.idou) {
          Logger.debug('新規作成モードの初期化を行います')
          form.value.idou = {
            idou_date: null,
            todokede_kubun: null,
            todokede1_naiyou: null,
            todokede2_naiyou: null,
          }
        } else {
          Logger.debug('編集モードの初期化を行います')
          workTimeId.value = form.value.shinsei_koyou_keiyaku_time.length > 0 ? form.value.shinsei_koyou_keiyaku_time[0].koyou_keiyaku_time_id : null
          koyouKeiyakuId.value = form.value.koyou_keiyaku_id ?? null
          Logger.debug('異動届より就業場所（雇用契約）と就業時間を初期化しました', { '雇用契約ID': koyouKeiyakuId.value, '就業時間ID': workTimeId.value })
        }
        isInit.value = true
        Logger.debug('異動届コンポーネントの初期化完了\n----------------------------')
      })
    })

    const todokede = computed(() => {
      if (!form.value.idou) return null;
      if (form.value.idou.todokede_kubun === OptionConst.TODOKEDE_KUBUN.JUUSYO_CHANGE) {
        return 'change-address'
      }
      if (form.value.idou.todokede_kubun === OptionConst.TODOKEDE_KUBUN.KAISEI) {
        return 'change-name'
      }
      return 'other'
    })

    const authStore = useAuthStore();

    const syozokuId = computed(() => {
      return authStore.user?.mst_syozoku_id
    })

    const todokedeKubunOption = computed(() => {
      if (syozokuId && syozokuId.value === OptionConst.SYOZOKU.PM) {
        return OptionConst.TODOKEDE_KUBUN_OPTION_PM;
      }
      else {
        return OptionConst.TODOKEDE_KUBUN_OPTION_PG;
      }
    })

    // 就業場所・就業時間が変更された場合に走る処理
    // 引数は以下コンポーネントのemits.changeに準拠
    // src/resources/ts/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue
    const changeKoyouKeiyakuTime = (emitData: KoyouKeiyakuTimeSelectEmitData) => {
      if (!form.value.idou) return

      // 就業場所に関する情報をemitする（申請オブジェクトの変更等は親に移譲する）
      context.emit('changeKoyouKeiyakuTime', emitData.selectedSyuugyouBasyo, null);
    }

    return {
      getError,
      removeAllError,
      todokede,
      'todokedeKubunOption': todokedeKubunOption,
      OptionConst,
      changeKoyouKeiyakuTime,
      koyouKeiyakuId,
      workTimeId,
      isInit
    }
  },
  components: {
    'change-name': NameChange,
    'change-address': AddressChange,
    'other': Other,
    'koyouKeiyakuTimeSelect': KoyouKeiyakuTimeSelect
  },
})

</script>
<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>